import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function SetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        const hash = window.location.hash || '';
        const search = window.location.search || '';
        const hashParams = new URLSearchParams(hash.replace('#', ''));
        const queryParams = new URLSearchParams(search.replace('?', ''));

        const errorCode = hashParams.get('error') || queryParams.get('error');
        const errorDesc = hashParams.get('error_description') || queryParams.get('error_description');
        const type = hashParams.get('type') || queryParams.get('type');
        const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token') || '';

        // Handle explicit error in URL
        if (errorCode) {
          const msg = errorDesc ? errorDesc.replace(/\+/g, ' ') : 'Your link has expired or is invalid.';
          setError(msg);
          return;
        }

        // FIRST: Check if Supabase already established a session via ConfirmationURL redirect
        // This happens when the email link uses {{ .ConfirmationURL }} — Supabase verifies
        // the token server-side and redirects here with an active session already set
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        if (existingSession?.user) {
          setUserEmail(existingSession.user.email || '');
          window.history.replaceState(null, '', '/set-password');
          setReady(true);
          return;
        }

        // Check for token_hash (custom template flow)
        const tokenHash = queryParams.get('token_hash') || hashParams.get('token_hash');
        if (tokenHash) {
          const { data, error: sessionError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type || 'recovery'
          });
          if (sessionError || !data?.session) {
            setError('Your link has expired. Please request a new one from the login page.');
            return;
          }
          setUserEmail(data.session.user?.email || '');
          window.history.replaceState(null, '', '/set-password');
          setReady(true);
          return;
        }

        // Check for PKCE code
        const code = queryParams.get('code') || hashParams.get('code');
        if (code) {
          const { data, error: sessionError } = await supabase.auth.exchangeCodeForSession(code);
          if (sessionError || !data?.session) {
            setError('Your link has expired. Please request a new one from the login page.');
            return;
          }
          setUserEmail(data.session.user?.email || '');
          window.history.replaceState(null, '', '/set-password');
          setReady(true);
          return;
        }

        // Legacy hash flow fallback
        if (accessToken) {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          if (sessionError || !data?.session) {
            setError('Your link has expired. Please request a new one from the login page.');
            return;
          }
          setUserEmail(data.session.user?.email || '');
          window.history.replaceState(null, '', '/set-password');
          setReady(true);
          return;
        }

        // Nothing found
        setError('No valid invite link found. Please use the link from your email or contact your administrator.');

      } catch (err) {
        console.error('SetPassword init error:', err);
        setError('Something went wrong. Please try the link again or contact your administrator.');
      }
    };

    init();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }

    setSaving(true);

    // Timeout after 15 seconds
    const timeoutId = setTimeout(() => {
      setSaving(false);
      setError('Request timed out. Please check your connection and try again.');
    }, 15000);

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      clearTimeout(timeoutId);
      if (updateError) throw updateError;
      setDone(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      clearTimeout(timeoutId);
      setError(err.message || 'Could not set password. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      minHeight: '100dvh', // Better mobile support
      background: 'var(--bg-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
      WebkitOverflowScrolling: 'touch' // iOS smooth scroll
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))',
            marginBottom: 20
          }}>
            <span style={{ fontSize: 24, fontWeight: 700, color: '#0a0a0a' }}>H</span>
          </div>
          <h1 style={{ fontSize: 28, color: 'var(--text-primary)', marginBottom: 8 }}>HMA Portal</h1>
          {userEmail && (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{userEmail}</p>
          )}
        </div>

        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 16, padding: 32
        }}>
          {/* Error state */}
          {error && !ready && (
            <div>
              <div className="alert alert-error" style={{ marginBottom: 20 }}>{error}</div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>
                Go to the login page and use "Forgot password?" to get a new link.
              </p>
              <button className="btn btn-secondary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => navigate('/login')}>
                Back to Login
              </button>
            </div>
          )}

          {/* Loading state */}
          {!error && !ready && !done && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: 40, height: 40,
                border: '3px solid var(--border)',
                borderTop: '3px solid var(--accent)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                Verifying your link...
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 12 }}>
                Taking too long?{' '}
                <span style={{ color: 'var(--accent)', cursor: 'pointer' }}
                  onClick={() => navigate('/login')}>
                  Go to login
                </span>
              </p>
            </div>
          )}

          {/* Success state */}
          {done && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(76,175,130,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px', fontSize: 28
              }}>✓</div>
              <h3 style={{ fontSize: 20, marginBottom: 8 }}>Password Set!</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                Taking you to your dashboard...
              </p>
            </div>
          )}

          {/* Password form */}
          {ready && !done && (
            <>
              <h2 style={{ fontSize: 20, marginBottom: 8 }}>Set Your Password</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>
                Choose a secure password to access your coaching portal.
              </p>
              {error && <div className="alert alert-error">{error}</div>}
              <form onSubmit={handleSubmit} autoComplete="new-password">
                <div className="form-group">
                  <label className="label">New Password</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    style={{ fontSize: 16 }} // Prevents iOS zoom on focus
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 24 }}>
                  <label className="label">Confirm Password</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="Repeat your password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                    style={{ fontSize: 16 }} // Prevents iOS zoom on focus
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 16 }}
                  disabled={saving}
                >
                  {saving ? 'Setting up your account...' : 'Set Password & Enter Portal'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
